import {
  AuthOnFile,
  JCodeDetails,
  VisitDataChart,
  VisitDataRow,
} from "../../../data-model/medicalPaOrdersModel";
import { MedicalPaOrdersAuthStatus } from "../../../enums/medicalPaOrdersAuthStatus";
import { parseMMDDYYYY } from "../../../utils/formatDate";
import { getAllJCodesFromAuthOnFile } from "./existingDocsToLs";

/**
 * Checks if multiple past regimens exist for a medical PA order.
 *
 * This function determines whether any J-codes with "Auth on File" status in the given medical PA order
 * have multiple past regimens. A past regimen is considered to exist if there is a data point in the
 * visit data charts that occurs more than 30 days after the J-code's start date.
 *
 * @param {AuthOnFile} authOnFileDetails - The auth on file details to check for multiple past regimens
 * @returns {boolean} Returns `true` if any J-code with "Auth on File" status has a past regimen data point
 *                    that is more than 30 days after its start date; otherwise returns `false`
 */
export const checkIfMultiplePastRegimenExists = (
  authOnFileDetails: AuthOnFile,
) => {
  // Get all J-codes from the order
  const jCodes = getAllJCodesFromAuthOnFile(authOnFileDetails);
  if (jCodes === undefined || !jCodes?.length) {
    return false;
  }

  // Filter for J-codes with "Auth on File" status
  const jCodesWithAuthOnFileStatus = getJCodesWithAuthOnFileStatus(
    jCodes,
  ).filter((jCode) => jCode?.num_visits !== 999);
  if (!jCodesWithAuthOnFileStatus?.length) {
    return false;
  }

  // Check if any J-code has multiple past regimens
  const visitDataCharts = authOnFileDetails?.visit_data_charts;

  return jCodesWithAuthOnFileStatus.some((jCode) =>
    checkIfMultiplePastRegimenExistsForJCode(jCode, visitDataCharts),
  );
};

/**
 * Filters J-codes to only those with "Auth on File" authorization status.
 *
 * @param jCodes - Array of J-code details to filter
 * @returns Subset of J-codes that have the AuthOnFile status
 */
const getJCodesWithAuthOnFileStatus = (jCodes: JCodeDetails[]) => {
  return jCodes.filter(
    (jCode) =>
      jCode && jCode.auth_status === MedicalPaOrdersAuthStatus.AuthOnFile,
  );
};

/**
 * Checks whether a single J-code has a past regimen by finding the
 * earliest visit data point after the J-code's start date and verifying
 * it falls more than 30 days later.
 *
 * @param jCodesWithAuthOnFileStatus - The J-code to evaluate
 * @param visitDataChartDetails - Visit chart array from auth-on-file
 * @returns `true` if a past regimen gap (>30 days) exists
 */
const checkIfMultiplePastRegimenExistsForJCode = (
  jCodesWithAuthOnFileStatus: JCodeDetails,
  visitDataChartDetails: VisitDataChart[] | undefined,
) => {
  const visitDataChartDetail = getVisitDataChartByDescription(
    visitDataChartDetails,
    jCodesWithAuthOnFileStatus?.medicine_name ??
      jCodesWithAuthOnFileStatus?.description ??
      "",
  );
  if (visitDataChartDetail && visitDataChartDetail.totalRows > 0) {
    const allDataPoints = visitDataChartDetail.data;
    const mostRecentNextDataPoint =
      getTheMostRecentNextDataPointFromJCodeFromDate(
        allDataPoints,
        jCodesWithAuthOnFileStatus?.start_date ?? "",
      );
    if (
      mostRecentNextDataPoint &&
      checkIfTheNextDataIsAfter30Days(
        mostRecentNextDataPoint,
        jCodesWithAuthOnFileStatus?.start_date ?? "",
      )
    ) {
      return true;
    }
  }
  return false;
};

/**
 * Resolves visit chart rows for a J-code description from the visit-data
 * chart array on auth-on-file.
 *
 * @param visitDataChartDetails - Visit chart array from auth-on-file
 * @param jCodeDescription - J-code description used to match chart data
 * @returns Normalized chart rows with row count when a match is found
 */
const getVisitDataChartByDescription = (
  visitDataChartDetails: VisitDataChart[] | undefined,
  jCodeDescription: string,
): { data: VisitDataRow[]; totalRows: number } | undefined => {
  if (!visitDataChartDetails?.length || !jCodeDescription) {
    return undefined;
  }

  const normalizedDescription = jCodeDescription.trim().toLowerCase();
  const chart = visitDataChartDetails.find(
    (visitDataChart: VisitDataChart) =>
      (visitDataChart?.medicine_name ?? "").trim().toLowerCase() ===
        normalizedDescription ||
      (visitDataChart?.medicine_name ?? "")
        .trim()
        .toLowerCase()
        .includes(normalizedDescription),
  );

  const chartRows = chart?.data?.data ?? [];
  const totalRows = chart?.total_rows ?? chart?.data?.total_rows ?? 0;
  return chart ? { data: chartRows, totalRows } : undefined;
};

/**
 * Finds the closest visit data point that occurs strictly after the given date.
 *
 * @param allDataPoints - All regimen dose data points
 * @param fromDate - Reference date in MM/DD/YYYY format
 * @returns The nearest future data point, or `undefined` if none exists
 */
const getTheMostRecentNextDataPointFromJCodeFromDate = (
  allDataPoints: VisitDataRow[],
  fromDate: string,
): VisitDataRow | undefined => {
  const fromDateTime = parseMMDDYYYY(fromDate).getTime();
  let closestFutureDataPoint: VisitDataRow | undefined = undefined;
  let minDiff = Infinity;

  for (const dataPoint of allDataPoints) {
    const dataPointTime = parseMMDDYYYY(dataPoint.Date).getTime();
    if (dataPointTime > fromDateTime) {
      const diff = dataPointTime - fromDateTime;
      if (diff < minDiff) {
        minDiff = diff;
        closestFutureDataPoint = dataPoint;
      }
    }
  }

  return closestFutureDataPoint;
};

/**
 * Returns `true` when the data point's date is more than 30 days after
 * the reference date, indicating a gap between regimen cycles.
 *
 * @param dataPoint - Visit data point to check
 * @param fromDate - Reference start date in MM/DD/YYYY format
 * @returns Whether the gap exceeds 30 days
 */
const checkIfTheNextDataIsAfter30Days = (
  dataPoint: VisitDataRow,
  fromDate: string,
) => {
  const dataPointTime = parseMMDDYYYY(dataPoint.Date).getTime();
  const fromDateTime = parseMMDDYYYY(fromDate).getTime();
  const thirtyDaysInMs = 30 * 24 * 60 * 60 * 1000;
  return dataPointTime > fromDateTime + thirtyDaysInMs;
};
